import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material'

import NoImage from './../../../assets/images/users/no-image.png'
import { IconLogout, IconSettings, IconBuildingStore, IconHome } from '@tabler/icons'
import { MainCard, Transitions } from './../../../components/Main'
import { removeCookies } from '../../../utility/Cookies'

const ProfileSection = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const customization = useSelector((state) => state.customization)
  const { seller } = useSelector((state) => state.seller)
  const navigate = useNavigate()
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  const handleListItemClick = (event, index, route = '') => {
    setSelectedIndex(index)
    handleClose(event)

    if (route && route !== '') {
      navigate(route)
    }
  }
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus()
    }

    prevOpen.current = open
  }, [open])

  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          transition: 'all .2s ease-in-out',
          background: 'transparent',
          borderColor: 'transparent',
          color: theme.palette.grey[700],
          '& svg': {
            stroke: theme.palette.grey[700],
          },
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.light,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
              stroke: theme.palette.grey[50],
            },
          },
          '& .MuiChip-label': {
            lineHeight: 0,
          },
        }}
        icon={
          <Avatar
            src={seller.store_logo || NoImage}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer',
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  elevation={16}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                >
                  <Box sx={{ p: 2, pb: 0 }}>
                    <Stack sx={{ pb: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">{seller.company_name}</Typography>
                      </Stack>

                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2" align="left">
                          {seller?.store_name}
                        </Typography>
                        <Typography variant="subtitle2" align="right">
                          {t(`title.${seller?.tax_type?.toLowerCase()}`)}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider />
                  </Box>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <List
                      component="nav"
                      sx={{
                        width: '100%',
                        maxWidth: 350,
                        minWidth: 300,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: '10px',
                        color: theme.palette.grey[50],
                        [theme.breakpoints.down('md')]: {
                          minWidth: '100%',
                        },
                        '& .MuiListItemButton-root': {
                          mt: 0.5,
                        },
                      }}
                    >
                      <ListItemButton
                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                        selected={selectedIndex === 0}
                        onClick={(event) => {
                          handleListItemClick(event, 0, '/account/store')
                        }}
                      >
                        <ListItemIcon>
                          <IconBuildingStore stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography>{t('title.my_store')}</Typography>} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                        selected={selectedIndex === 1}
                        onClick={(event) => {
                          handleListItemClick(event, 1, '/account/company')
                        }}
                      >
                        <ListItemIcon>
                          <IconHome stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Typography>{t('title.company_profile')}</Typography>}
                        />
                      </ListItemButton>
                      <ListItemButton
                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                        selected={selectedIndex === 2}
                        onClick={(event) => {
                          handleListItemClick(event, 2, '/account/settings')
                        }}
                      >
                        <ListItemIcon>
                          <IconSettings stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography>{t('menu.settings')}</Typography>} />
                      </ListItemButton>
                      <ListItemButton
                        sx={{ borderRadius: `${customization.borderRadius}px` }}
                        selected={selectedIndex === 3}
                        onClick={() => {
                          removeCookies('_wsso')
                          removeCookies('_wssc')
                          removeCookies('_wchat')
                          removeCookies('wsc-summary-header')
                          window.location = '/account/signin'
                        }}
                      >
                        <ListItemIcon>
                          <IconLogout stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <ListItemText primary={<Typography>{t('menu.signout')}</Typography>} />
                      </ListItemButton>
                    </List>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  )
}

export default ProfileSection
